import React, {useState, useEffect} from 'react';
import ReactDOM from 'react-dom';
import {useLocale} from '../Localization/LocaleContext';

const AppDownloadDialog = () => {
  const {translate: t} = useLocale();
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    // Check if dialog was already shown
    const dialogShown = localStorage.getItem('appDownloadDialogShown');
    if (!dialogShown) {
      setIsOpen(true);
    }
  }, []);

  const handleClose = () => {
    setIsOpen(false);
    // Mark dialog as shown so it doesn't appear again
    localStorage.setItem('appDownloadDialogShown', 'true');
  };

  if (!isOpen) {
    return null;
  }

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-950/65 p-4 backdrop-blur-sm">
      <div className="relative w-full max-w-[620px] overflow-hidden rounded-[28px] border border-white/10 bg-[#17191d] text-white shadow-[0_28px_80px_rgba(0,0,0,0.48)]">
        <div className="pointer-events-none absolute -right-20 -top-20 h-64 w-64 rounded-full bg-teal-400/15 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-32 -left-16 h-56 w-56 rounded-full bg-indigo-500/15 blur-3xl" />

        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute right-5 top-5 z-10 grid h-10 w-10 place-items-center rounded-full border border-white/10 bg-white/5 text-slate-300 transition-colors hover:bg-white/10 hover:text-white focus:outline-none focus:ring-2 focus:ring-teal-300"
          aria-label={t('navigation.app_download.close', 'Close')}>
          <svg
            className="h-5 w-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>

        <div className="relative p-7 sm:p-9">
          <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-teal-300/20 bg-teal-300/10 px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.16em] text-teal-200">
            <span className="h-1.5 w-1.5 rounded-full bg-teal-300" />
            {t('navigation.app_download.eyebrow', 'Mobile companion')}
          </div>

          <h2 className="max-w-md text-3xl font-semibold tracking-tight text-white sm:text-4xl">
            {t('navigation.app_download.title', 'Keep your project moving.')}
          </h2>

          <p className="mt-4 max-w-lg text-base leading-7 text-slate-300">
            {t('navigation.app_download.description', 'Handle the essentials from your phone—from approving new listings to checking orders and keeping your business on track.')}
          </p>

          <div className="mt-6 grid gap-2.5 text-sm text-slate-200 sm:grid-cols-3">
            {[
              ['approve_listings', 'Approve listings'],
              ['manage_orders', 'Manage orders'],
              ['check_performance', 'Check performance'],
            ].map((benefit) => (
              <div
                key={benefit[0]}
                className="flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.04] px-3 py-2.5">
                <span className="grid h-5 w-5 shrink-0 place-items-center rounded-full bg-teal-300/15 text-teal-200">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="h-3 w-3">
                    <path strokeLinecap="round" strokeLinejoin="round" d="m5 12 4 4L19 6" />
                  </svg>
                </span>
                <span>{t(`navigation.app_download.${benefit[0]}`, benefit[1])}</span>
              </div>
            ))}
          </div>

          <div className="mt-8 border-t border-white/10 pt-6">
            <p className="mb-3 text-sm font-medium text-slate-300">
              {t('navigation.app_download.download_title', 'Download the SuperAdmin app')}
            </p>
            <div className="flex flex-col gap-3 sm:flex-row">
              <a
                href="https://apps.apple.com/us/app/tradly-superadmin/id6756290651"
                target="_blank"
                rel="noopener noreferrer"
                className="flex flex-1 items-center gap-3 rounded-xl border border-white/10 bg-slate-900 px-5 py-3.5 text-white shadow-lg transition hover:-translate-y-0.5 hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-teal-300">
                <svg
                  className="w-6 h-6 flex-shrink-0"
                  viewBox="0 0 24 24"
                  fill="currentColor">
                  <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
                </svg>
                <div className="text-left">
                  <div className="text-[10px] uppercase tracking-wide text-slate-400">{t('navigation.app_download.download_on', 'Download on the')}</div>
                  <div className="-mt-0.5 text-base font-semibold">{t('navigation.app_download.app_store', 'App Store')}</div>
                </div>
              </a>
              <a
                href="https://play.google.com/store/apps/details?id=tradly.app"
                target="_blank"
                rel="noopener noreferrer"
                className="flex flex-1 items-center gap-3 rounded-xl border border-white/10 bg-slate-900 px-5 py-3.5 text-white shadow-lg transition hover:-translate-y-0.5 hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-teal-300">
                <svg
                  className="w-6 h-6 flex-shrink-0"
                  viewBox="0 0 24 24"
                  fill="currentColor">
                  <path d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 01-.61-.92V2.734a1 1 0 01.609-.92zm10.89 10.893l2.302 2.302-10.937 6.333 8.635-8.635zm3.199-3.198l2.807 1.626a1 1 0 010 1.73l-2.808 1.626L15.206 12l2.492-2.491zM5.864 2.658L16.8 8.99l-2.302 2.302-8.634-8.634z" />
                </svg>
                <div className="text-left">
                  <div className="text-[10px] uppercase tracking-wide text-slate-400">{t('navigation.app_download.get_it_on', 'Get it on')}</div>
                  <div className="-mt-0.5 text-base font-semibold">Google Play</div>
                </div>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default AppDownloadDialog;
